<template>
  <div
    class="profile_dropdown border_rounded_pill text-no-wrap px-3 py-2"
    @click.stop="showMenu = true"
    @keydown.stop="showMenu = true"
  >
    <div class="position-relative">
      <div v-if="notificationsTotalCount > 0" class="notifications__count position-absolute">
        {{ notificationsTotalCount }}
      </div>
      <Avatar :pathImg="navInfo?.profileImg" size="sm" />
    </div>

    <span class="medium-body-sm typography_default">
      {{ navInfo.name.split(' ')[0] }}
    </span>
    <Icon class="margin-left_xs-df" icon="menu" color="#64748B" />

    <div v-if="showMenu" class="profile_dropdown_menu">
      <div v-for="(menuList, i) in menuLists" :key="i" class="profile-menu__section py-2">
        <div
          v-for="item in menuList"
          :key="item.title"
          class="profile_dropdown_menu_item regular-body-sm py-3 px-5"
          :style="`${item.title === 'Logout' ? 'justify-content: flex-start;' : ''}`"
          @click="itemClicked(item)"
          @keydown="itemClicked(item)"
        >
          <span>
            {{ item.title }}
          </span>
          <div v-if="item?.showNotifications" class="notifications__count">
            {{ item?.showNotifications }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Avatar from '@design_system_v3/components/atoms/Avatar.vue';
import Icon from '@design_system_v2/atoms/Icon.vue';
import { mapGetters } from 'vuex';
import { tracker } from '@services';
import { getResetPasswordUrl } from '@common/services/auth';

export default {
  name: 'ProfileComponent',
  emits: ['open-change-user-modal'],
  components: {
    Avatar,
    Icon,
  },
  data() {
    return {
      showMenu: false,
    };
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  },
  methods: {
    handleOutsideClick() {
      this.showMenu = false;
    },
    async itemClicked(item) {
      if (item.title === 'Change password') {
        tracker('profile_click', {
          page: item.title,
        });
        await getResetPasswordUrl().then((resetUrl) => {
          window.location.href = resetUrl;
        }).catch(() => {
          window.postMessage({
            name: 'iConnectionsEventBus',
            type: 'showSnackbar',
            message: 'An unexpected error has ocurred',
            snackbarType: 'error',
          }, '*');
        });
        return;
      }

      if (!item.path) {
        return;
      }

      tracker('profile_click', {
        page: item.title,
      });

      if (item.path === '/logout') {
        window.postMessage({ name: 'iConnectionsEventBus', type: 'logout' }, '*');
        return;
      }

      if (item.path === '/change_user') {
        this.$emit('open-change-user-modal');
        return;
      }

      window.postMessage({ name: 'iConnectionsEventBus', type: 'changeMFe', path: item.path }, '*');
    },
  },
  computed: {
    ...mapGetters('main', [
      'navInfo',
      'infoUser',
      'notificationsCount',
      'userProfile',
      'featureFlags',
    ]),
    menuLists() {
      const menuSections = [];

      const firstSection = [
        {
          title: 'Chats',
          path: '/messages',
          showNotifications: this.notificationsCount?.unreadMessagesCount,
        },
        {
          title: 'Meetings',
          path: '/inbox',
          showNotifications: this.notificationsCount.pendingMeetingsCount,
        },
      ];

      const secondSection = [];
      const thirdSection = [{ title: 'Profile', path: `/user/${this?.infoUser?.contactId}` }];

      if (this.infoUser.CompanyTypeID === 1) {
        const myFunds = `/funds/company/${this?.infoUser?.companyId}/overview`;
        secondSection.push({ title: 'Funds', path: myFunds });
        firstSection.push({ title: 'Allocator activity', path: '/user/allocator_activity', showNotifications: false });
      }

      if (this.infoUser.CompanyTypeID === 2) {
        const companyId = this.userProfile?.companyId;
        const investorPreferenceId = this.userProfile?.investorPreferenceId;
        const myInvestorPreference = `/allocators/company/${companyId}/allocator/${investorPreferenceId}/overview`;
        secondSection.push({ title: 'Watch List', path: '/funds/watchlist' });
        thirdSection.push({ title: 'Preferences', path: myInvestorPreference });
        firstSection.push({ title: 'Company profile views', path: '/user/who_viewed_my_company', showNotifications: false });
      }

      thirdSection.push({ title: 'Settings', path: '/user/profile_settings/profile' });

      // Only show this for iConnection and radcliffefunds (ICP-12047) accounts
      const emailDomain = this.infoUser.email.split('@')[1].toLowerCase();
      if (emailDomain === 'iconnections.io' || emailDomain === 'radcliffefunds.com') {
        thirdSection.push({ title: 'Change user', path: '/change_user' });
      }

      if (this.infoUser.CompanyTypeID === 4) {
        thirdSection.push(
          {
            title: 'Copilot v2',
            path: 'https://copilot.iconnections.io',
            event: 'navbar_copilot_click',
          },
          {
            title: 'Copilot v3',
            path: 'https://copilotv3.iconnections.io',
            event: 'navbar_copilot3_click',
          },
        );
      }
      menuSections.push(firstSection);
      if (secondSection.length) {
        menuSections.push(secondSection);
      }
      menuSections.push(thirdSection);
      menuSections.push([
        { title: 'Change password' },
        { title: 'Logout', path: '/logout' },
      ]);

      return menuSections;
    },
    notificationsTotalCount() {
      return (
        this.notificationsCount.unreadMessagesCount
        + this.notificationsCount.pendingMeetingsCount
      );
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@foundation';

.cursor-pointer {
  cursor: pointer !important;
}

.profile_dropdown {
  display: flex;
  gap: 8px;
  position: relative;
  align-items: center;
  cursor: pointer;
  background-color: map-get($neutral, 'neutral-100');
  border: 1px solid map-get($neutral, 'neutral-400');
  height: fit-content;

  &--selected {
    background-color: map-get($primary, 'purple-500');
  }

  &--notification {
    position: absolute;
    top: 0;
    right: 7px;
  }

  &:hover {
    background-color: map-get($neutral, 'neutral-200');
  }
}

.profile_dropdown_menu {
  @extend .background_neutral_white;
  @extend .border_rounded_lg;

  height: auto;
  width: 300px;
  position: absolute;
  top: 50px;
  right: 0;
  z-index: 10010;
  overflow: hidden;
  box-shadow: 2px 4px 24px -4px map-get($neutral, 'neutral-200');
}

.profile_dropdown_menu_item {
  cursor: pointer;
  display: flex;
  gap: 10px;
  align-items: center;

  &:hover {
    background-color: map-get($neutral, 'neutral-100');
  }
}

.profile-menu__section {
  border-bottom: 1px solid map-get($neutral, 'neutral-200');

  &:last-child {
    border-bottom: 0;
  }
}

.notifications__count {
  background-color: #d33d59;
  border-radius: 50%;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  bottom: 18px;
  left: 18px;
  font-weight: 600;
  font-size: 10px;
  z-index: 1;
  width: 20px;
  height: 20px;
}
</style>
